/**
 * In-app YouTube video play page using embed (no redirect to youtube.com).
 */

import {useCallback} from 'react'
import {Linking, Platform, ScrollView, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {type CommonNavigatorParams} from '#/lib/routes/types'
import {atoms as a, useTheme} from '#/alf'
import * as Layout from '#/components/Layout'
import {Button, ButtonText} from '#/components/Button'
import {Text} from '#/components/Typography'

const EMBED_BASE = 'https://www.youtube.com/embed/'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'YouTubeVideo'>

function buildEmbedUrl(videoId: string): string {
  const params = new URLSearchParams({
    autoplay: '1',
    rel: '0',
    modestbranding: '1',
  })
  return `${EMBED_BASE}${videoId}?${params.toString()}`
}

function YouTubeEmbedWeb({videoId}: {videoId: string}) {
  const src = buildEmbedUrl(videoId)
  return (
    <View style={[a.w_full, {aspectRatio: 16 / 9, backgroundColor: '#000'}]}>
      <iframe
        src={src}
        title="YouTube video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 'none',
        }}
      />
    </View>
  )
}

function YouTubeEmbedNative({videoId}: {videoId: string}) {
  const {WebView} = require('react-native-webview')
  const uri = buildEmbedUrl(videoId)
  return (
    <View style={[a.w_full, {aspectRatio: 16 / 9, backgroundColor: '#000'}]}>
      <WebView
        source={{uri}}
        style={{flex: 1}}
        allowsFullscreenVideo
        allowsInlineMediaPlayback
        mediaPlaybackRequiresUserAction={false}
      />
    </View>
  )
}

export function YouTubeVideoScreen({route}: Props) {
  const {videoId, title, channelTitle} = route.params
  const {_} = useLingui()
  const t = useTheme()

  const openInYouTube = useCallback(() => {
    Linking.openURL(`https://www.youtube.com/watch?v=${videoId}`).catch(() => {})
  }, [videoId])

  return (
    <Layout.Screen>
      <Layout.Center style={[a.flex_1, t.atoms.bg]}>
        <ScrollView
          style={[a.flex_1]}
          contentContainerStyle={[a.p_md, a.gap_md]}>
          <View style={[a.rounded_lg, a.overflow_hidden, t.atoms.border_contrast_low, a.border]}>
            {Platform.OS === 'web' ? (
              <YouTubeEmbedWeb videoId={videoId} />
            ) : (
              <YouTubeEmbedNative videoId={videoId} />
            )}
          </View>
        {(title != null || channelTitle != null) && (
          <View style={[a.gap_xs]}>
            {title != null && title !== '' && (
              <Text style={[a.text_lg, a.font_semi_bold, t.atoms.text]} numberOfLines={3}>
                {title}
              </Text>
            )}
            {channelTitle != null && channelTitle !== '' && (
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]} numberOfLines={1}>
                {channelTitle}
              </Text>
            )}
          </View>
        )}
        <Button
          label={_(msg`Open in YouTube`)}
          onPress={openInYouTube}
          color="secondary"
          size="small">
          <ButtonText>
            <Trans>Open in YouTube</Trans>
          </ButtonText>
        </Button>
        </ScrollView>
      </Layout.Center>
    </Layout.Screen>
  )
}
